import { useEffect, useState, useCallback } from 'react';
import { X, Music, AlertCircle, Edit3, Check, Video, Sparkles, Loader2 } from 'lucide-react';
import Button from '../common/Button';
import OctopusLoader, { type ProcessingStage } from '../common/OctopusLoader';
import DropZone from './DropZone';
import VideoCreator from '../video/VideoCreator';
import { useTrackUpload } from '../../hooks/useTrackUpload';
import { api } from '../../services/api';
import type { TrackContext } from '../../types';

interface TrackUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  personaId: string;
  onUploadComplete?: () => void;
}

type ModalStage = 'upload' | 'creating_video';

export default function TrackUploadModal({ isOpen, onClose, personaId, onUploadComplete }: TrackUploadModalProps) {
  const { state, file, error, processingStage, elapsedSeconds, estimatedSeconds, analysisResult, trackContextId, selectFile, startUpload, cancelUpload, reset } = useTrackUpload(personaId);

  // Modal-level state
  const [modalStage, setModalStage] = useState<ModalStage>('upload');
  const [videoCreatorTrack, setVideoCreatorTrack] = useState<TrackContext | null>(null);
  const [isLoadingTrack, setIsLoadingTrack] = useState(false);
  const [loadTrackError, setLoadTrackError] = useState<string | null>(null);

  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  // Extract clean title from filename (remove timestamp and uuid prefix)
  const getCleanTitle = (filename: string) => {
    const parts = filename.split('-');
    if (parts.length >= 3) {
      const nameWithExt = parts.slice(2).join('-');
      return nameWithExt.replace(/\.[^/.]+$/, '');
    }
    return filename.replace(/\.[^/.]+$/, '');
  };

  const displayTitle = editedTitle || (analysisResult?.filename ? getCleanTitle(analysisResult.filename) : file?.name ? getCleanTitle(file.name) : 'Track');

  // Reset everything when modal closes - force reset even from complete state
  const resetAll = useCallback(() => {
    setModalStage('upload');
    setVideoCreatorTrack(null);
    setIsLoadingTrack(false);
    setLoadTrackError(null);
    setIsEditingTitle(false);
    setEditedTitle('');
    reset(true); // Force reset - user explicitly closing modal
  }, [reset]);

  // Handle modal close
  const handleClose = useCallback(() => {
    // During video creation, just go back to upload stage
    if (modalStage === 'creating_video') {
      setModalStage('upload');
      setVideoCreatorTrack(null);
      return;
    }

    // During upload/processing, cancel first
    if (state === 'uploading' || state === 'processing') {
      cancelUpload();
    }

    resetAll();
    onClose();
  }, [modalStage, state, cancelUpload, resetAll, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        // Block escape during uploading/processing
        if (state === 'uploading' || state === 'processing') {
          return;
        }
        // During video creation or completion, escape goes back
        if (modalStage === 'creating_video' || state === 'complete') {
          if (modalStage === 'creating_video') {
            setModalStage('upload');
            setVideoCreatorTrack(null);
          }
          return;
        }
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, state, modalStage, handleClose]);

  // Notify parent when upload completes
  useEffect(() => {
    if (state === 'complete') {
      onUploadComplete?.();
    }
  }, [state, onUploadComplete]);

  // DISABLED: Auto-proceed was causing infinite loop with trim-recommendations
  // User should click "Create Video" button manually to proceed
  // The dancing octopus celebration screen stays visible until user action

  // Handle continuing to video creator
  const handleContinueToVideo = async () => {
    if (!trackContextId) return;

    setIsLoadingTrack(true);
    setLoadTrackError(null);

    try {
      // Fetch the full track data
      const response = await api.getTrack(trackContextId);

      if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to load track');
      }

      setVideoCreatorTrack(response.data);
      setModalStage('creating_video');
    } catch (err) {
      console.error('Failed to load track for video creation:', err);
      setLoadTrackError(err instanceof Error ? err.message : 'Failed to load track');
    } finally {
      setIsLoadingTrack(false);
    }
  };

  // Handle video creation complete
  const handleVideoComplete = (videoUrl: string) => {
    console.log('Video created:', videoUrl);
    // Stay in video creator to show the result
    // User can close manually or go back
  };

  // Handle going back from video creator
  const handleBackFromVideo = () => {
    setModalStage('upload');
    setVideoCreatorTrack(null);
  };

  const handleStartEditTitle = () => {
    setEditedTitle(displayTitle);
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!trackContextId || !editedTitle.trim()) return;

    setIsSavingTitle(true);
    try {
      await api.updateTrackTitle(trackContextId, editedTitle.trim());
      setIsEditingTitle(false);
    } catch (err) {
      console.error('Failed to save title:', err);
    }
    setIsSavingTitle(false);
  };

  const handleUploadAnother = () => {
    setIsEditingTitle(false);
    setEditedTitle('');
    setLoadTrackError(null);
    reset(true); // Force reset - user explicitly clicked "Upload Another"
  };

  const handleFileSelect = (selectedFile: File) => {
    selectFile(selectedFile);
  };

  const handleUpload = async () => {
    await startUpload();
  };

  // Handle backdrop click
  const handleBackdropClick = () => {
    // Block during uploading/processing
    if (state === 'uploading' || state === 'processing') {
      return;
    }
    // During video creation, backdrop does nothing (use X or back button)
    if (modalStage === 'creating_video') {
      return;
    }
    // During completion, backdrop does nothing (use buttons)
    if (state === 'complete') {
      return;
    }
    handleClose();
  };

  if (!isOpen) return null;

  // Render VideoCreator when in that stage
  if (modalStage === 'creating_video' && videoCreatorTrack) {
    return (
      <VideoCreator
        isOpen={true}
        onClose={handleBackFromVideo}
        track={videoCreatorTrack}
        personaId={personaId}
        onComplete={handleVideoComplete}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleBackdropClick} />

      {/* Modal */}
      <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h2 className="text-lg font-semibold">
            {state === 'complete' ? '✨ Track Ready!' : state === 'processing' ? 'Analyzing...' : state === 'uploading' ? 'Uploading...' : 'Upload Track'}
          </h2>
          {state !== 'complete' && (
            <button
              onClick={handleClose}
              className="p-1 rounded-lg hover:bg-zinc-800 transition-colors"
              title={state === 'uploading' || state === 'processing' ? 'Cancel upload' : 'Close'}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Idle state - show drop zone */}
          {(state === 'idle' || state === 'selected') && (
            <>
              <DropZone onFileSelect={handleFileSelect} className="mb-4" />

              {file && (
                <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg mb-4">
                  <div className="w-10 h-10 rounded-lg bg-jelly-500/20 flex items-center justify-center">
                    <Music className="w-5 h-5 text-jelly-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-zinc-500">
                      {formatFileSize(file.size)}
                      {estimatedSeconds && (
                        <span className="ml-2 text-jelly-400">
                          ~ {estimatedSeconds < 60 ? `${estimatedSeconds}s` : `${Math.floor(estimatedSeconds / 60)}:${String(estimatedSeconds % 60).padStart(2, '0')}`} to analyze
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Uploading & Processing states */}
          {(state === 'uploading' || state === 'processing') && (
            <div className="py-4">
              <OctopusLoader
                message={state === 'uploading' ? 'Uploading track...' : 'Analyzing track...'}
                stage={
                  state === 'uploading'
                    ? 'uploading'
                    : (processingStage === 'quick_scan' || !processingStage)
                      ? 'chunking'
                      : (processingStage as ProcessingStage)
                }
                elapsedSeconds={elapsedSeconds}
                estimatedSeconds={estimatedSeconds}
                showAllStages={true}
              />
            </div>
          )}

          {/* Complete state */}
          {state === 'complete' && (
            <div className="py-4">
              {/* Celebration Header */}
              <div className="flex flex-col items-center mb-6">
                <div className="w-32 h-32 mb-4 relative">
                  <div className="absolute -top-2 -left-4 text-2xl confetti-1">🎉</div>
                  <div className="absolute -top-1 -right-2 text-xl confetti-2">🎊</div>
                  <div className="absolute top-4 -left-6 text-lg confetti-3">✨</div>
                  <div className="absolute top-2 -right-6 text-lg confetti-4">⭐</div>

                  <svg viewBox="0 0 100 100" className="w-full h-full happy-octopus-dance">
                    <ellipse cx="50" cy="40" rx="25" ry="22" fill="url(#happyOctopusGradient)" />
                    <path d="M35 36 Q40 40 45 36" fill="none" stroke="#1a1a2e" strokeWidth="2.5" strokeLinecap="round" />
                    <path d="M55 36 Q60 40 65 36" fill="none" stroke="#1a1a2e" strokeWidth="2.5" strokeLinecap="round" />
                    <path d="M38 48 Q50 60 62 48" fill="none" stroke="#1a1a2e" strokeWidth="2.5" strokeLinecap="round" />
                    <circle cx="32" cy="44" r="5" fill="#ff9999" opacity="0.6" />
                    <circle cx="68" cy="44" r="5" fill="#ff9999" opacity="0.6" />
                    <text x="75" y="25" fontSize="12" className="sparkle-1">✨</text>
                    <text x="20" y="30" fontSize="10" className="sparkle-2">✨</text>
                    <text x="70" y="55" fontSize="8" className="sparkle-3">✨</text>
                    <path d="M30 58 Q28 72 26 80" stroke="url(#happyOctopusGradient)" strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-dance-1" />
                    <path d="M38 60 Q36 74 34 82" stroke="url(#happyOctopusGradient)" strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-dance-2" />
                    <path d="M50 62 Q50 76 50 85" stroke="url(#happyOctopusGradient)" strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-dance-3" />
                    <path d="M62 60 Q64 74 66 82" stroke="url(#happyOctopusGradient)" strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-dance-4" />
                    <path d="M70 58 Q72 72 74 80" stroke="url(#happyOctopusGradient)" strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-dance-5" />
                    <defs>
                      <linearGradient id="happyOctopusGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#e84a8a" />
                        <stop offset="100%" stopColor="#d63384" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                <div className="text-center">
                  <h3 className="text-xl font-bold text-white mb-1 flex items-center justify-center gap-2">
                    <Sparkles className="w-5 h-5 text-jelly-400" />
                    Ready for Video Creation!
                    <Sparkles className="w-5 h-5 text-jelly-400" />
                  </h3>
                  <p className="text-sm text-zinc-400">Your track has been analyzed in {elapsedSeconds}s</p>
                </div>
              </div>

              {/* Analysis Summary */}
              {analysisResult && (
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-zinc-700">
                    <Music className="w-4 h-4 text-jelly-400 flex-shrink-0" />
                    {isEditingTitle ? (
                      <div className="flex-1 flex items-center gap-2">
                        <input
                          type="text"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          className="flex-1 bg-zinc-900 border border-zinc-600 rounded px-2 py-1 text-sm text-zinc-200 focus:outline-none focus:border-jelly-500"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveTitle();
                            if (e.key === 'Escape') setIsEditingTitle(false);
                          }}
                        />
                        <button
                          onClick={handleSaveTitle}
                          disabled={isSavingTitle}
                          className="p-1 text-green-400 hover:text-green-300 disabled:opacity-50"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-zinc-200 truncate">{displayTitle}</span>
                        <button
                          onClick={handleStartEditTitle}
                          className="p-1 text-zinc-500 hover:text-zinc-300 flex-shrink-0"
                          title="Edit title"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>

                  <p className="text-[10px] text-zinc-600 -mt-2">ID: {trackContextId}</p>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-zinc-900/50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-jelly-400">{analysisResult.duration ? `${Math.floor(analysisResult.duration / 60)}:${String(Math.floor(analysisResult.duration % 60)).padStart(2, '0')}` : '--:--'}</p>
                      <p className="text-xs text-zinc-500">Duration</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-jelly-400">{analysisResult.tempo ? Math.round(analysisResult.tempo) : '--'}</p>
                      <p className="text-xs text-zinc-500">BPM</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-lg p-2 text-center">
                      <p className="text-sm font-medium text-zinc-200 truncate">{analysisResult.genre || 'Unknown'}</p>
                      <p className="text-xs text-zinc-500">Genre</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-lg p-2 text-center">
                      <p className="text-sm font-medium text-zinc-200 truncate">{analysisResult.mood || 'Unknown'}</p>
                      <p className="text-xs text-zinc-500">Mood</p>
                    </div>
                  </div>

                  {analysisResult.lyrics && (
                    <div className="pt-2 border-t border-zinc-700">
                      <p className="text-xs text-green-400 mb-1">Lyrics detected</p>
                      <p className="text-xs text-zinc-400 italic line-clamp-2">"{analysisResult.lyrics.slice(0, 100)}..."</p>
                    </div>
                  )}
                </div>
              )}

              {/* Error loading track for video */}
              {loadTrackError && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">{loadTrackError}</p>
                </div>
              )}

              {/* Animations */}
              <style>{`
                .happy-octopus-dance { animation: happyBounce 0.5s ease-in-out infinite; }
                @keyframes happyBounce {
                  0%, 100% { transform: translateY(0) rotate(-2deg); }
                  50% { transform: translateY(-8px) rotate(2deg); }
                }
                .tentacle-dance-1 { animation: tentacleDance 0.4s ease-in-out infinite; animation-delay: 0s; }
                .tentacle-dance-2 { animation: tentacleDance 0.4s ease-in-out infinite; animation-delay: 0.08s; }
                .tentacle-dance-3 { animation: tentacleDance 0.4s ease-in-out infinite; animation-delay: 0.16s; }
                .tentacle-dance-4 { animation: tentacleDance 0.4s ease-in-out infinite; animation-delay: 0.24s; }
                .tentacle-dance-5 { animation: tentacleDance 0.4s ease-in-out infinite; animation-delay: 0.32s; }
                @keyframes tentacleDance {
                  0%, 100% { transform: rotate(-8deg); transform-origin: top center; }
                  50% { transform: rotate(8deg); transform-origin: top center; }
                }
                .sparkle-1 { animation: sparkle 1s ease-in-out infinite; }
                .sparkle-2 { animation: sparkle 1s ease-in-out infinite; animation-delay: 0.3s; }
                .sparkle-3 { animation: sparkle 1s ease-in-out infinite; animation-delay: 0.6s; }
                @keyframes sparkle {
                  0%, 100% { opacity: 1; transform: scale(1); }
                  50% { opacity: 0.5; transform: scale(0.8); }
                }
                .confetti-1 { animation: confettiFall 2s ease-in-out infinite; }
                .confetti-2 { animation: confettiFall 2s ease-in-out infinite; animation-delay: 0.3s; }
                .confetti-3 { animation: confettiFall 2s ease-in-out infinite; animation-delay: 0.6s; }
                .confetti-4 { animation: confettiFall 2s ease-in-out infinite; animation-delay: 0.9s; }
                @keyframes confettiFall {
                  0%, 100% { transform: translateY(0) rotate(0deg); opacity: 1; }
                  50% { transform: translateY(-10px) rotate(15deg); opacity: 0.8; }
                }
              `}</style>
            </div>
          )}

          {/* Error state */}
          {state === 'error' && (
            <div className="py-8">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
                <p className="text-sm text-zinc-300 mb-1">Upload failed</p>
                <p className="text-xs text-red-400 text-center px-4">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-zinc-800">
          {state === 'idle' && (
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          )}

          {state === 'selected' && (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleUpload}>Upload & Analyze</Button>
            </>
          )}

          {(state === 'uploading' || state === 'processing') && (
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          )}

          {state === 'complete' && (
            <div className="flex w-full justify-between">
              <Button variant="secondary" onClick={handleUploadAnother}>
                Upload Another
              </Button>
              <Button onClick={handleContinueToVideo} disabled={isLoadingTrack} className="gap-2">
                {isLoadingTrack ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Video className="w-4 h-4" />
                    Create Video
                  </>
                )}
              </Button>
            </div>
          )}

          {state === 'error' && (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={() => reset(true)}>Try Again</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
